// @system — Landing page features section: numbered feature cards (01–04) with embedded visuals
// Design reference: unosend.co — 2x2 feature grid with mockup visuals inside each card
// Heading and subheading default to product-neutral copy; LandingPage passes the product's own
// (content/@custom/landing.js featuresTitle / featuresSubtitle). Never the template's pitch.
// @custom — to customise, pass `features` prop or import and wrap with your own data
import { Zap, Shield, BarChart3, CreditCard } from 'lucide-react'

// ── Embedded visual mockups (like unosend's progress bars, code snippets, dashboards) ──

function DeliveryMetrics() {
  return (
    <div className="mt-5 space-y-3">
      {[
        { label: 'API response time', value: '98.2%', pct: 98 },
        { label: 'Error rate', value: '0.3%', pct: 3 },
        { label: 'Cache hit rate', value: '94.1%', pct: 94 },
      ].map(({ label, value, pct }) => (
        <div key={label}>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-brand-text-muted">{label}</span>
            <span className="font-medium text-brand-text">{value}</span>
          </div>
          <div className="h-1.5 rounded-full bg-brand-primary/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-brand-primary transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      ))}
      <div className="grid grid-cols-3 gap-3 pt-2 border-t border-[var(--brand-border-subtle)]/50">
        {[
          { val: '4 Regions', sub: 'Multi-cloud' },
          { val: '12 Nodes', sub: 'Auto-scaling' },
          { val: '<200ms', sub: 'P99 latency' },
        ].map(({ val, sub }) => (
          <div key={val} className="text-center">
            <div className="text-xs font-semibold text-brand-text">{val}</div>
            <div className="text-[10px] text-brand-text-muted">{sub}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function DashboardMockup() {
  return (
    <div className="mt-5 rounded-lg border border-[var(--brand-border-subtle)]/50 bg-[var(--brand-bg)]/50 overflow-hidden">
      <div className="flex border-b border-[var(--brand-border-subtle)]/50 text-[10px]">
        {['Overview', 'Users', 'Billing', 'API Keys'].map((tab, i) => (
          <span
            key={tab}
            className={`px-3 py-2 ${i === 0 ? 'text-brand-text font-medium border-b border-primary' : 'text-brand-text-muted'}`}
          >
            {tab}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-px bg-border/30">
        {[
          { label: 'Users', value: '2,489', change: '+12%' },
          { label: 'Revenue', value: '$8.4k', change: '+23%' },
          { label: 'API calls', value: '142k', change: '+8%' },
          { label: 'Active', value: '1,891', change: '+5%' },
        ].map(({ label, value, change }) => (
          <div key={label} className="bg-[var(--brand-bg)]/50 px-2.5 py-2.5">
            <div className="text-[10px] text-brand-text-muted">{label}</div>
            <div className="text-sm font-semibold text-brand-text">{value}</div>
            {/* --color-success is held at WCAG AA against the theme background by apply-brand (statusColors) */}
            <div className="text-[10px] text-[var(--color-success)]">{change}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function CodeSnippet() {
  return (
    <div className="mt-5">
      <div className="rounded-lg border border-[var(--brand-border-subtle)]/50 bg-brand-surface p-4 font-mono text-[11px] leading-relaxed overflow-hidden">
        <div><span className="text-[var(--color-info)] dark:text-[var(--color-info)]">import</span> <span className="text-[var(--color-success)]">{'{ createClient }'}</span> <span className="text-[var(--color-info)] dark:text-[var(--color-info)]">from</span> <span className="text-[var(--color-warning)] dark:text-[var(--color-warning)]">&apos;your-product/sdk&apos;</span></div>
        <div className="mt-2"><span className="text-brand-primary">const</span> <span className="text-[var(--color-info)] dark:text-[var(--color-info)]">client</span> = <span className="text-[var(--color-success)]">createClient</span>({'{'}</div>
        <div className="pl-4"><span className="text-[var(--color-info)] dark:text-[var(--color-info)]">apiKey</span>: <span className="text-[var(--color-warning)] dark:text-[var(--color-warning)]">&apos;sk_live_...&apos;</span></div>
        <div>{'}'})</div>
        <div className="mt-2"><span className="text-brand-primary">const</span> <span className="text-[var(--color-info)] dark:text-[var(--color-info)]">user</span> = <span className="text-[var(--color-info)] dark:text-[var(--color-info)]">await</span> client.<span className="text-[var(--color-success)]">users</span>.<span className="text-[var(--color-success)]">create</span>({'{'}</div>
        <div className="pl-4"><span className="text-[var(--color-info)] dark:text-[var(--color-info)]">email</span>: <span className="text-[var(--color-warning)] dark:text-[var(--color-warning)]">&apos;dev@example.com&apos;</span>,</div>
        <div className="pl-4"><span className="text-[var(--color-info)] dark:text-[var(--color-info)]">plan</span>: <span className="text-[var(--color-warning)] dark:text-[var(--color-warning)]">&apos;pro&apos;</span></div>
        <div>{'}'})</div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {['REST API', 'Node.js', 'Python', 'Go', 'Ruby'].map((lang) => (
          <span key={lang} className="rounded-full border border-[var(--brand-border-subtle)]/50 bg-brand-surface px-2 py-0.5 text-[10px] text-brand-text-muted">
            {lang}
          </span>
        ))}
      </div>
    </div>
  )
}

function EventFeed() {
  const events = [
    { time: 'now', label: 'User signed up', status: 'success' },
    { time: '2s ago', label: 'Payment processed', status: 'success' },
    { time: '5s ago', label: 'API key created', status: 'success' },
    { time: '12s ago', label: 'Team invite sent', status: 'info' },
    { time: '18s ago', label: 'Webhook delivered', status: 'success' },
  ]
  return (
    <div className="mt-5 space-y-2">
      {events.map(({ time, label, status }, i) => (
        <div key={i} className="flex items-center gap-2.5 text-xs">
          <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${
            i === 0 ? 'bg-[var(--color-success)] animate-pulse motion-reduce:animate-none' :
            status === 'success' ? 'bg-[var(--color-success)]/60' : 'bg-[var(--color-info)]/60'
          }`} />
          <span className="text-brand-text-muted w-12 flex-shrink-0 font-mono text-[10px]">{time}</span>
          <span className="text-brand-text truncate">{label}</span>
        </div>
      ))}
      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[var(--brand-border-subtle)]/50 mt-3">
        {[
          { val: '99.9%', sub: 'Uptime' },
          { val: '<30ms', sub: 'Response' },
          { val: '24/7', sub: 'Monitoring' },
        ].map(({ val, sub }) => (
          <div key={val} className="text-center">
            <div className="text-xs font-semibold text-brand-text">{val}</div>
            <div className="text-[10px] text-brand-text-muted">{sub}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

const VISUAL_MAP = {
  metrics: DeliveryMetrics,
  dashboard: DashboardMockup,
  code: CodeSnippet,
  events: EventFeed,
}

/**
 * @typedef {object} LandingFeature
 * @property {IconComponent} icon
 * @property {string} title
 * @property {string} description
 * @property {keyof typeof VISUAL_MAP} [visual] - Embedded mockup rendered under the copy
 */

/**
 * The default cards describe what every product built on this template really has, with no numbers
 * and no mockup: the embedded visuals above carry invented figures (98.2 %, 2,489 users, $8.4k, a fake
 * event feed, a sample SDK) and are rendered ONLY when a product passes `visual` on its own features
 * (content/@custom/landing.js) because the figure is its own. A new site shows no invented claim
 * (dev-rankradar, 2026-10-12: the SaaS template's dashboard mockup on an SEO product's home page).
 * @type {LandingFeature[]}
 */
const DEFAULT_FEATURES = [
  {
    icon: Shield,
    title: 'Your account, protected',
    description:
      'Sign in with e-mail or Google, optional two-step verification, and a session that stays yours.',
  },
  {
    icon: Zap,
    title: 'Works on every device',
    description:
      'The same experience on a phone, a tablet and a desktop, light or dark, with no app to install.',
  },
  {
    icon: BarChart3,
    title: 'Everything in one place',
    description:
      'Your workspace, your settings and your billing live in one dashboard, not spread across tools.',
  },
  {
    icon: CreditCard,
    title: 'Clear plans, no surprises',
    description:
      'What you pay is on the pricing page. Change or cancel from your own billing page at any time.',
  },
]

/** @param {{ features?: LandingFeature[], heading?: string, subheading?: string }} props */
export function FeaturesSection({
  features = DEFAULT_FEATURES,
  heading = 'What you get',
  subheading = 'The core of the product, ready from the first sign-in.',
}) {
  return (
    <section id="features" className="container mx-auto px-4 py-16 sm:py-20 md:py-24">
      <div className="text-center mb-10 sm:mb-14">
        <p className="text-xs font-medium uppercase tracking-widest text-brand-text-muted mb-3">
          Features
        </p>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
          {heading}
        </h2>
        <p className="mt-3 text-sm sm:text-base text-brand-text-muted max-w-xl mx-auto leading-relaxed">
          {subheading}
        </p>
      </div>

      <div className="grid gap-5 sm:gap-6 grid-cols-1 md:grid-cols-2 max-w-5xl mx-auto">
        {features.map(({ icon: Icon, title, description, visual }, index) => {
          const VisualComponent = visual ? VISUAL_MAP[visual] : null
          return (
            <div
              key={title}
              className="group relative rounded-xl border border-[var(--brand-border-subtle)] bg-brand-surface p-5 sm:p-7 transition-all hover:shadow-md hover:border-primary/20"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-primary/10">
                  <Icon className="h-4 w-4 text-brand-primary" />
                </div>
                <span className="text-xs font-mono font-medium text-[var(--brand-text-muted)] tracking-widest uppercase">
                  {String(index + 1).padStart(2, '0')}
                </span>
              </div>

              <h3 className="font-semibold text-lg mt-3 mb-2">{title}</h3>
              <p className="text-sm text-brand-text-muted leading-relaxed">
                {description}
              </p>

              {VisualComponent && <VisualComponent />}
            </div>
          )
        })}
      </div>
    </section>
  )
}
